import CustomLogin from "@app/components/Login.vue"
import { Store as SharedStore } from "@app/kernel/store"
import { OrganizationService } from "@app/services/organization.service"
import { diag } from "@hoppscotch/common/kernel/log"
import { getService } from "@hoppscotch/common/modules/dioc"
import {
  AuthEvent,
  AuthPlatformDef,
  HoppUser,
} from "@hoppscotch/common/platform/auth"
import {
  PersistenceService,
  STORE_KEYS,
} from "@hoppscotch/common/services/persistence"
import { getApp } from "firebase/app"
import {
  User as FBUser,
  getAuth,
  initializeAuth,
  isSignInWithEmailLink as isSignInWithEmailLinkFB,
  onAuthStateChanged,
  onIdTokenChanged,
  sendEmailVerification,
  signInWithEmailLink as signInWithEmailLinkFB,
  signOut,
  updateEmail,
  updateProfile,
} from "firebase/auth"
import { doc, getFirestore, onSnapshot, setDoc } from "firebase/firestore"
import * as E from "fp-ts/Either"
import {
  BehaviorSubject,
  Subject,
  Subscription,
  combineLatest,
  filter,
  map,
} from "rxjs"

export const currentUserFB$ = new BehaviorSubject<FBUser | null>(null)
export const authEvents$ = new Subject<AuthEvent>()
export const probableUser$ = new BehaviorSubject<HoppUser | null>(null)

const authIdToken$ = new BehaviorSubject<string | null>(null)

async function signInWithEmailLink(email: string, url: string) {
  return await signInWithEmailLinkFB(getAuth(), email, url)
}

function fbUserToHoppUser(user: FBUser): HoppUser {
  return {
    uid: user.uid,
    displayName: user.displayName,
    email: user.email,
    photoURL: user.photoURL,
    emailVerified: user.emailVerified,
  }
}

const currentUser$ = new BehaviorSubject<HoppUser | null>(null)

const persistenceService = getService(PersistenceService)

// shared (non-org-scoped) store for login state. initialized in
// performAuthInit, used by setUser and onIdTokenChanged to keep the
// shared store in sync so org webviews can pick up existing auth
let sharedAuthStore: Awaited<ReturnType<typeof SharedStore.extend>> | null =
  null

async function setUser(user: HoppUser | null) {
  currentUser$.next(user)
  probableUser$.next(user)

  // callers (e.g. setDisplayName) invoke setUser without await, so a
  // rejection from sharedAuthStore.set would surface as an unhandled
  // rejection and silently kill the calling work. log and swallow:
  // in-memory state is already updated above, so the only loss is the
  // new state won't survive an app restart
  if (sharedAuthStore) {
    try {
      await sharedAuthStore.set("login_state", user)
    } catch (e) {
      diag("auth", "setUser persist failed:", e)
    }
  }
}

/**
 * Reauthenticate the user with the given credential
 */
async function reauthenticateUser() {
  // TODO: Figure out reauthentication
}

export function getUserFirebaseMetadata() {
  return currentUserFB$.value?.metadata
}

export const def: AuthPlatformDef = {
  customLoginSelectorUI: CustomLogin,
  getCurrentUserStream: () => currentUser$,
  getAuthEventsStream: () => authEvents$,
  getProbableUserStream: () => probableUser$,

  getCurrentUser: () => currentUser$.value,
  getProbableUser: () => probableUser$.value,

  getBackendHeaders() {
    const orgID = getService(OrganizationService).getOrganizationID()

    return {
      authorization: `Bearer ${authIdToken$.value}`,
      ...(orgID ? { "x-organization-id": orgID } : {}),
    }
  },
  waitOrganizationInfoReady() {
    return getService(OrganizationService).waitOrganizationInfoReady()
  },
  axiosPlatformConfig() {
    const orgID = getService(OrganizationService).getOrganizationID()

    return {
      headers: {
        Authorization: `Bearer ${authIdToken$.value}`,
        ...(orgID ? { "x-organization-id": orgID } : {}),
      },
    }
  },
  willBackendHaveAuthError() {
    return !authIdToken$.value
  },
  onBackendGQLClientShouldReconnect(func) {
    authIdToken$.subscribe(() => {
      func()
    })
  },
  getDevOptsBackendIDToken() {
    return authIdToken$.value
  },
  async performAuthInit() {
    const orgParam = new URLSearchParams(window.location.search).get("org")
    const orgTag = orgParam || "vendored"
    diag("auth", `performAuthInit started [${orgTag}]`)
    diag("auth", "href:", window.location.href)
    // desktop-only: routes Firebase auth tokens through the unified
    // (non-org-scoped) kernel store so auth state is shared across all
    // webviews. when a user logs in via the vendored webview, every org
    // webview inherits the session automatically without prompting for
    // login again. the web platform doesn't need this because each org
    // is a separate subdomain with its own browser storage.
    //
    // uses SharedStore (hoppscotch-unified.store) instead of the per-org
    // common Store, because auth is user-scoped not org-scoped. the
    // per-org store would isolate tokens per webview, forcing a
    // redundant login on every org switch.
    //
    // Previously used indexedDBLocalPersistence here as a workaround for a
    // Firebase/Linux issue (firebase-js-sdk#5019). The kernel store adapter
    // avoids IndexedDB entirely so that workaround is no longer needed.
    const scoped = await SharedStore.extend("firebase-auth.v1")
    sharedAuthStore = scoped

    class KernelPersistence {
      static type = "LOCAL" as const
      type = "LOCAL" as const

      async _isAvailable() {
        return scoped.isAvailable()
      }
      async _set(key: string, value: unknown) {
        return scoped.set(key, value)
      }
      async _get<T>(key: string) {
        return scoped.get<T>(key)
      }
      async _remove(key: string) {
        return scoped.remove(key)
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _addListener(_key: string, _listener: unknown) {
        // No-op: kernel store is per-instance, no cross-tab sync required atm,
        // might require one in the future hence TODO: look into this
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _removeListener(_key: string, _listener: unknown) {
        // No-op
      }
    }

    const auth = initializeAuth(getApp(), {
      persistence: KernelPersistence as any,
    })
    const firestore = getFirestore()

    combineLatest([currentUserFB$, authIdToken$])
      .pipe(
        map(([user, token]) => {
          // If there is no auth token, we will just consider as the auth as not complete
          if (token === null) return null
          if (user !== null) return fbUserToHoppUser(user)
          return null
        })
      )
      .subscribe((x) => {
        currentUser$.next(x)
      })

    let extraSnapshotStop: (() => void) | null = null

    // read login state from the unified (non-org-scoped) store only.
    // auth is user-scoped not org-scoped, so the per-org store
    // (persistenceService) is not used for login state. previously the
    // code wrote to both stores and read per-org first, but logout on
    // one webview couldn't clear other orgs' per-org stores, leaving
    // stale LOGIN_STATE entries that hydrated probableUser$ and caused
    // waitProbableLoginToConfirm to hang waiting for a token that
    // Firebase couldn't restore from the (correctly cleared) unified
    // store.
    //
    // backwards compat: old data was stored as a JSON.stringify'd
    // string via setLocalConfig, new data is stored as a raw object
    // via set<T>(). if the value comes back as a string, parse it.
    const loginState = await scoped.get<HoppUser | string>("login_state")
    diag("auth", `loginState resolved [${orgTag}]:`, {
      type: typeof loginState,
      isNull: loginState === null,
      isUndefined: loginState === undefined,
      hasValue: !!loginState,
    })

    if (typeof loginState === "string") {
      probableUser$.next(JSON.parse(loginState) as HoppUser | null)
    } else {
      probableUser$.next(loginState ?? null)
    }

    onAuthStateChanged(auth, async (user) => {
      diag("auth", `onAuthStateChanged [${orgTag}]:`, {
        hasUser: !!user,
        uid: user?.uid ?? "(none)",
      })
      const wasLoggedIn = currentUser$.value !== null

      if (user) {
        probableUser$.next(user)
      } else {
        probableUser$.next(null)

        if (sharedAuthStore) {
          await sharedAuthStore.remove("login_state")
        }
      }

      if (!user && extraSnapshotStop) {
        extraSnapshotStop()
        extraSnapshotStop = null
      } else if (user) {
        // Merge all the user info from all the authenticated providers
        user.providerData.forEach((profile) => {
          if (!profile) return

          const us = {
            updatedOn: new Date(),
            provider: profile.providerId,
            name: profile.displayName,
            email: profile.email,
            photoUrl: profile.photoURL,
            uid: profile.uid,
          }

          setDoc(doc(firestore, "users", user.uid), us, { merge: true }).catch(
            (e) => console.error("error updating", us, e)
          )
        })

        extraSnapshotStop = onSnapshot(
          doc(firestore, "users", user.uid),
          (doc) => {
            const data = doc.data()

            const userUpdate: HoppUser = fbUserToHoppUser(user)

            if (data) {
              // Write extra provider data
              userUpdate.provider = data.provider
              userUpdate.accessToken = data.accessToken
            }

            currentUser$.next(userUpdate)
          }
        )
      }

      currentUserFB$.next(user)
      currentUser$.next(user === null ? null : fbUserToHoppUser(user))

      // User wasn't found before, but now is there (login happened)
      if (!wasLoggedIn && user) {
        authEvents$.next({
          event: "login",
          user: currentUser$.value!,
        })
      } else if (wasLoggedIn && !user) {
        // User was found before, but now is not there (logout happened)
        authEvents$.next({
          event: "logout",
        })
      }
    })

    onIdTokenChanged(auth, async (user) => {
      diag("auth", `onIdTokenChanged [${orgTag}]:`, {
        hasUser: !!user,
        uid: user?.uid ?? "(none)",
      })
      if (user) {
        // token fetch and persistence are split into separate try/catch blocks
        // because a persistence failure (kernel store write) shouldn't revoke a
        // valid token. if getIdToken fails the token stays null and
        // authIdToken$ emits null, which callers like waitProbableLoginToConfirm
        // filter out (they only resolve on truthy tokens) so they fall through
        // to their own timeout rather than getting an immediate failure signal.
        // if persistence fails the token is still valid in memory, it just
        // won't survive an app restart
        let token: string | null = null
        try {
          token = await user.getIdToken()
        } catch (e) {
          diag("auth", `getIdToken failed [${orgTag}]:`, e)
        }
        authIdToken$.next(token)

        if (token) {
          try {
            if (sharedAuthStore) {
              await sharedAuthStore.set("login_state", user)
            }
          } catch (e) {
            diag("auth", `persist login state failed [${orgTag}]:`, e)
          }
        }
      } else {
        authIdToken$.next(null)
      }
    })
  },

  waitProbableLoginToConfirm() {
    return new Promise<void>((resolve, reject) => {
      if (authIdToken$.value) {
        resolve()
        return
      }

      if (!probableUser$.value) {
        reject(new Error("no_probable_user"))
        return
      }

      // without a timeout this promise hangs forever if Firebase can't
      // restore the session (e.g. expired refresh token, kernel store
      // corruption). 30s is long enough for a normal token refresh but
      // short enough that the user isn't stuck watching "Logging you in..."
      // indefinitely. callers like org service catch the rejection and set
      // organizationError to "auth/fail" so the route guard redirects to
      // login instead of hanging
      const AUTH_TIMEOUT_MS = 30_000

      let sub: Subscription | null = null
      const timer = setTimeout(() => {
        sub?.unsubscribe()
        diag(
          "auth",
          `waitProbableLoginToConfirm timed out after ${AUTH_TIMEOUT_MS}ms`
        )
        reject(new Error("auth_timeout"))
      }, AUTH_TIMEOUT_MS)

      sub = authIdToken$.pipe(filter((token) => !!token)).subscribe(() => {
        clearTimeout(timer)
        sub?.unsubscribe()
        resolve()
      })
    })
  },

  async signInWithEmail() {
    throw new Error("Not applicable")
  },

  isSignInWithEmailLink(url: string) {
    return isSignInWithEmailLinkFB(getAuth(), url)
  },

  async verifyEmailAddress() {
    if (!currentUserFB$.value) throw new Error("No user has logged in")

    try {
      await sendEmailVerification(currentUserFB$.value)
    } catch (e) {
      console.error("error verifying email address", e)
      throw e
    }
  },
  async signInUserWithGoogle() {
    throw new Error("Not applicable")
  },
  async signInUserWithGithub() {
    throw new Error("Not applicable")
  },
  async signInUserWithMicrosoft() {
    throw new Error("Not applicable")
  },
  async signInWithEmailLink(email: string, url: string) {
    await signInWithEmailLinkFB(getAuth(), email, url)
  },
  async setEmailAddress(email: string) {
    if (!currentUserFB$.value) throw new Error("No user has logged in")

    try {
      await updateEmail(currentUserFB$.value, email)
    } catch (e) {
      await reauthenticateUser()
      console.log("error setting email address", e)
      throw e
    }
  },
  async setDisplayName(name: string) {
    if (!name) return E.left("USER_NAME_CANNOT_BE_EMPTY")
    if (!currentUserFB$.value) return E.left("NO_USER_LOGGED_IN")

    const us = {
      displayName: name,
    }

    try {
      await updateProfile(currentUserFB$.value, us)
      setUser({
        ...fbUserToHoppUser(currentUserFB$.value),
        displayName: name,
      })
      return E.right(undefined)
    } catch (e) {
      console.error("error updating display name", e)
      return E.left(e)
    }
  },
  async signOutUser() {
    if (!currentUser$.value) throw new Error("No user has logged in")

    await signOut(getAuth())
  },
  // Forces a new ID token from Firebase instead of the cached one. The GQL
  // client calls this when the backend answers with an auth error, and three
  // failed refreshes in a row sign the user out through `authRetryGuard`.
  // Firebase refuses the refresh for a user it has disabled or whose refresh
  // tokens were revoked. A network failure isn't an answer about the user, so
  // it reports success and the request fails on its own instead of counting
  // toward the sign-out. `onIdTokenChanged` above publishes the new token to
  // `getBackendHeaders`.
  async refreshAuthToken() {
    const user = getAuth().currentUser
    if (!user) return false

    try {
      await user.getIdToken(true)
      return true
    } catch (e) {
      diag("auth", "forced ID token refresh failed:", e)
      return (e as { code?: string })?.code === "auth/network-request-failed"
    }
  },
  async processMagicLink() {
    if (this.isSignInWithEmailLink(window.location.href)) {
      let email = await persistenceService.getNullable<string>(
        STORE_KEYS.EMAIL_FOR_SIGN_IN
      )

      if (!email) {
        email = window.prompt(
          "Please provide your email for confirmation"
        ) as string
      }

      await signInWithEmailLink(email, window.location.href)

      await persistenceService.remove(STORE_KEYS.EMAIL_FOR_SIGN_IN)
      window.location.href = "/"
    }
  },
  getAllowedAuthProviders() {
    return Promise.resolve(E.right(["GITHUB", "GOOGLE", "MICROSOFT", "EMAIL"]))
  },

  /**
   * This is used to determine whether the email address is editable by the user or not.
   * This is enabled only for the cloud instance.
   */
  isEmailEditable: true,
}
